/*
 * assets/article.js — the in-article shortcode renderer.
 * Workstream 07, item 07-W13 (burn-down S5). Spec 07 §7.7, §9 item 8, criteria 5 and 23.
 *
 * Three shortcodes, one renderer, two templates
 * --------------------------------------------
 * The grammar is unchanged, which is the whole point: 542 published articles carry it and
 * not one of them is edited.
 *
 *   [[T13,T18]]      a product row, one Shopify tag per item
 *   [[T13,T18,6]]    the same, capped at six products across all of its tags
 *   [[ABC-1234]]     a product row, matched on variant SKU (a token containing "-")
 *   [[…|show_message=true]]   say so when nothing matches, instead of collapsing
 *   [[ctaquiz]]      the state-aware call to action
 *
 * On the standard article template sections/sts-article.liquid rewrites those into roots in
 * Liquid before the body is printed. On Bloggle, which prints {{ article.content }} itself,
 * assets/sts-bloggle-enhance.js does the same against the DOM. Both produce
 * `<div class="article__product-slider--root" data-tags="…">`, so this file is the one
 * renderer for both.
 *
 * What changed in S5, and why
 * ---------------------------
 * This file used to hold a hard-coded shop domain, a pinned Storefront API version
 * (`https://stspoc.myshopify.com/api/2023-04/graphql.json`) and a Storefront access token
 * read out of the page. The token is leaked — it sat in git history and was served in the
 * HTML of every article with a shortcode — and it is rotated once this lands (burn-down
 * A9). So the Storefront API is gone entirely rather than re-pointed:
 *
 *   tags   {collectionsUrl}/{tagCollection}/<tag>?section_id=sts-shortcode-products
 *   SKUs   {searchUrl}?q=<sku>&resources[options][fields]=variants.sku&section_id=…
 *
 * Both are same-origin, unauthenticated, unversioned, and carry the shopper's session, so
 * a signed-in customer's fit verdict arrives already decided. Every path comes from
 * #article-config (snippets/sts-shortcode-config.liquid), which renders them from Liquid's
 * `routes`; the fallbacks below are built from window.Shopify.routes.root so a locale
 * prefix survives either way. There is no shop domain and no API version in this file.
 *
 * The other half of the change is what a row is made of. It used to be a bespoke card
 * built from a GraphQL response — its own markup, its own price formatting, no idea
 * whether the product suited the reader. Now the response IS the card: the section renders
 * snippets/sts-product-card.liquid, the one card every v4 grid and row uses, with the fit
 * verdict from snippets/sts-product-fit.liquid. An in-article card cannot disagree with
 * the collection page about the same product, because it is the same card.
 *
 * Swiper is no longer used. The row is the shared .sts-scroller, which is CSS on a phone
 * and a grid with arrow buttons on a desktop, wired by assets/sts-ui.js.
 */
(function (w, d) {
  'use strict';

  /* Both templates produce the same root. `.productCard--loader` is one of the four
     skeleton children the standard template prints inside it, and is mapped back to its
     parent below — the old loop reassigned its own loop variable to do that, which throws
     in strict mode. */
  var ROOTS = '.article__product-slider--root, .productCard--loader';

  var TYPES = 'DSPT DSNT DSPW DSNW OSPT OSNT OSPW OSNW ORPT ORNT ORPW ORNW DRPT DRNT DRPW DRNW'.split(' ');
  var SUBS = { S1: 'Acne', S2: 'Redness', S3: 'Stinging', S4: 'Allergic' };
  /* D<->O, S<->R, P<->N, W<->T — the flip table snippets/sts-skin-types.liquid and
     assets/sts-collection.js both use. */
  var FLIP = { D: 'O', O: 'D', S: 'R', R: 'S', P: 'N', N: 'P', W: 'T', T: 'W' };

  function $(sel, ctx) { return (ctx || d).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || d).querySelectorAll(sel)); }
  function trim(s) { return String(s == null ? '' : s).trim(); }

  function warn(message, error) {
    try { console.warn('[sts-article-shortcode] ' + message, error || ''); } catch (e) { /* never break a page */ }
  }

  /* ------------------------------------------------------------------ config */

  var cfg = null;

  function configured() {
    if (cfg) return cfg;
    var base = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';

    cfg = {
      section: 'sts-shortcode-products',
      collectionsUrl: base + 'collections',
      tagCollection: 'all',
      searchUrl: base + 'search/suggest',
      cartAddUrl: base + 'cart/add',
      cartUrl: base + 'cart',
      emptyMessage: 'No products available for now'
    };

    /* Only the keys above are read. `storefrontAccessToken`, which older copies of the
       config block still carry, is deliberately ignored: nothing here talks to the
       Storefront API any more. */
    var block = d.getElementById('article-config');
    if (block && block.textContent) {
      var given = null;
      try { given = JSON.parse(block.textContent); } catch (e) { warn('#article-config is not valid JSON', e); }
      if (given) {
        var key;
        for (key in cfg) {
          if (!Object.prototype.hasOwnProperty.call(cfg, key)) continue;
          if (typeof given[key] === 'string' && given[key]) cfg[key] = given[key];
        }
      }
    }
    return cfg;
  }

  /* ---------------------------------------------------------- the shortcode */

  /* "T13,T18,6 | show_message=true" -> the tokens, the cap and whether an empty row says
     so. A trailing whole number is the cap, exactly as it was, and is only taken as one
     when something else is left to ask for. */
  function parseCommand(raw) {
    var text = String(raw == null ? '' : raw);
    var bar = text.indexOf('|');
    var message = bar > -1 ? trim(text.slice(bar + 1)) : '';
    var tokens = (bar > -1 ? text.slice(0, bar) : text).split(',').map(trim).filter(Boolean);
    var total = 10;

    if (tokens.length > 1 && /^\d+$/.test(tokens[tokens.length - 1])) {
      total = parseInt(tokens.pop(), 10) || 10;
    }
    return { tokens: tokens, total: total, message: message };
  }

  /* Shopify's tag URLs take the handleized tag. Every code in use is alphanumeric
     ("T13", "S03T", "PD02"), so this is exact for them and safe for the rest. */
  function handleize(value) {
    return trim(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function isSku(token) {
    return token.indexOf('-') > -1;
  }

  function tagUrl(token, section) {
    var c = configured();
    return c.collectionsUrl + '/' + encodeURIComponent(c.tagCollection) + '/' +
      encodeURIComponent(handleize(token)) + '?section_id=' + encodeURIComponent(section);
  }

  /* Predictive search restricted to the SKU field. The fuzzy match is narrowed to an
     exact one inside the section, so a near miss never lands in an article. */
  function skuUrl(token, limit, section) {
    var c = configured();
    return c.searchUrl + '?q=' + encodeURIComponent(token) +
      '&resources%5Btype%5D=product' +
      '&resources%5Blimit%5D=' + encodeURIComponent(limit) +
      '&resources%5Boptions%5D%5Bfields%5D=variants.sku' +
      '&section_id=' + encodeURIComponent(section);
  }

  function fetchSection(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('http_' + r.status)); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  /* --------------------------------------------------------- guest verdicts */

  /* Identical to assets/sts-collection.js, which is identical to
     snippets/sts-product-fit.liquid: hard bars first (a letter no approved type carries),
     then the letters whose single flip lands on an approved type, then '*'. Three copies of
     one rule is two too many, but the alternative is an in-article card that disagrees with
     the collection page, and the three are pinned to each other by the comment in each. */
  function barredLetter(approved, type) {
    var i, at, candidate;
    for (i = 0; i < 4; i++) {
      at = type.charAt(i);
      if (approved.indexOf(at) === -1) return at;
    }
    for (i = 0; i < 4; i++) {
      at = type.charAt(i);
      candidate = type.slice(0, i) + FLIP[at] + type.slice(i + 1);
      if (approved.indexOf(candidate) > -1) return at;
    }
    return '*';
  }

  function myType() {
    var s = w.STS && w.STS.state;
    var type = (s && s.type) || null;
    return type && TYPES.indexOf(type) > -1 ? type : null;
  }

  /* The sensitivity list is a device fact rather than one of workstream 02's five state
     facts, so it is read straight out of storage, in either shape the quiz has written
     over the years. Private-mode Safari throws on the read, not only on the write. */
  function mySubs() {
    var raw = null;
    try { raw = w.localStorage.getItem('customerSensitivityList'); } catch (e) { return []; }
    if (!raw) return [];
    var parsed = null;
    try { parsed = JSON.parse(raw); } catch (e) { parsed = raw.split(','); }
    if (!parsed || !parsed.length) return [];
    var out = [];
    for (var i = 0; i < parsed.length; i++) {
      var code = trim(parsed[i]).toUpperCase();
      if (SUBS[code]) out.push(code);
    }
    return out;
  }

  function missingSubs(cardSubs, wanted) {
    var out = [];
    for (var i = 0; i < wanted.length; i++) {
      if (cardSubs.indexOf(wanted[i]) === -1) out.push(SUBS[wanted[i]]);
    }
    return out;
  }

  /* For a signed-in customer the section already rendered the verdict; only fit="unknown"
     cards — guests — are finished here. */
  function resolveCards(root, reasons) {
    var type = myType();
    if (!type) return;
    var wanted = mySubs();

    $$('[data-sts-card][data-sts-fit="unknown"]', root).forEach(function (card) {
      var approved = (card.getAttribute('data-sts-approved') || '').split(/\s+/).filter(Boolean);
      var fit;
      var sentence = '';

      if (approved.indexOf(type) === -1) {
        fit = 'barred';
        var letter = barredLetter(approved, type);
        card.setAttribute('data-sts-bar', letter);
        /* v6.2 §11: the product's own note first (data-sts-notes). */
        var own = null;
        try { own = JSON.parse(card.getAttribute('data-sts-notes') || 'null'); } catch (e) { own = null; }
        sentence = (own && own[letter]) || (reasons && (reasons[letter] || reasons['*'])) || '';
      } else {
        var missing = missingSubs((card.getAttribute('data-sts-sens') || '').split(/\s+/).filter(Boolean), wanted);
        if (missing.length) {
          fit = 'subtype';
          sentence = 'Not approved for the ' + missing.join(', ') + '-prone form of sensitive skin.';
        } else {
          fit = 'approved';
        }
      }

      card.setAttribute('data-sts-fit', fit);
      if (sentence) {
        var slot = $('[data-sts-reason]', card);
        if (slot) slot.textContent = sentence;
      }
      if (fit !== 'approved') {
        var prop = $('[data-sts-not-for-type]', card);
        if (prop) { prop.value = type; prop.disabled = !type; }
      }
    });
  }

  /* ------------------------------------------------------------------- cart */

  function showDialog(dlg) {
    if (typeof dlg.showModal === 'function') { dlg.showModal(); return; }
    dlg.classList.add('sts-modal--fallback');
    dlg.setAttribute('open', '');
    var scrim = d.createElement('div');
    scrim.className = 'sts-modal-scrim';
    scrim.addEventListener('click', function () { hideDialog(dlg); });
    d.body.appendChild(scrim);
    dlg.__scrim = scrim;
    var first = $('a[href], button', dlg);
    if (first) first.focus();
  }

  function hideDialog(dlg) {
    if (typeof dlg.close === 'function' && dlg.open && !dlg.__scrim) { dlg.close(); return; }
    dlg.removeAttribute('open');
    if (dlg.__scrim && dlg.__scrim.parentNode) dlg.__scrim.parentNode.removeChild(dlg.__scrim);
    dlg.__scrim = null;
  }

  /* The same confirm the collection card opens (decision 04-Q31): a barred product never
     loses its buy button, it asks once and records the override. */
  function confirmDialog(text, onYes, onNo, sub) {
    var dlg = d.createElement('dialog');
    dlg.className = 'sts-modal';
    dlg.innerHTML =
      '<div class="sts-modal__inner">' +
      '<h2 class="sts-modal__title">' + (sub ? 'Not recommended for your sensitivity' : 'Not for your skin type') + '</h2>' +
      '<p class="sts-modal__body"></p>' +
      '<button type="button" class="sts-btn sts-btn--solid sts-btn--block" data-yes>Buy it anyway</button>' +
      '<button type="button" class="sts-btn sts-btn--ghost sts-btn--block" data-no>No thanks</button>' +
      '</div>';
    /* v6.2 §11: Dr. Baumann's reason and nothing after it. */
    $('.sts-modal__body', dlg).textContent = text || 'Dr. Baumann has not approved this product for your Baumann Skin Type.';
    d.body.appendChild(dlg);
    /* v6.1 1.2: ! octagon, the reason behind "Note from Dr. Baumann ▾". */
    var P = w.STS && w.STS.popups;
    if (P && P.notFor) { P.notFor($('.sts-modal__title', dlg), $('.sts-modal__body', dlg)); $('[data-yes]', dlg).autofocus = true; }

    function done() { hideDialog(dlg); if (dlg.parentNode) dlg.parentNode.removeChild(dlg); }
    $('[data-yes]', dlg).addEventListener('click', function () { done(); onYes(); });
    $('[data-no]', dlg).addEventListener('click', function () { done(); if (onNo) onNo(); });
    dlg.addEventListener('cancel', function () { if (onNo) onNo(); });
    dlg.addEventListener('close', function () { if (dlg.parentNode) dlg.parentNode.removeChild(dlg); });
    showDialog(dlg);
  }

  /* `_sts_src` (spec 09 §3.7) names where the line came from. Written into the form as a
     hidden input, not only into the FormData, so the ordinary form post this falls back to
     carries it too. */
  function setSource(form, src) {
    var input = $('input[name="properties[_sts_src]"]', form);
    if (!input) {
      input = d.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_sts_src]';
      form.appendChild(input);
    }
    input.value = src;
  }

  function addToCart(form, btn, src) {
    setSource(form, src || 'article_shortcode');
    btn.setAttribute('aria-busy', 'true');
    return fetch(configured().cartAddUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) { return r.json().then(function (j) { return r.ok ? j : Promise.reject(j); }); })
      .then(function (item) {
        btn.removeAttribute('aria-busy');
        /* No toast (v5.1 §1.5): the button confirms, as the article callout's does. */
        if (!btn.__stsLabel) btn.__stsLabel = btn.textContent;
        btn.textContent = 'Added';
        w.clearTimeout(btn.__stsT);
        btn.__stsT = w.setTimeout(function () { btn.textContent = btn.__stsLabel; }, 2500);
        d.dispatchEvent(new CustomEvent('sts:cart:added', { detail: item }));
      })
      .catch(function () {
        /* Never leave the reader stuck on a card that will not buy: hand the purchase back
           to the ordinary form post. */
        btn.removeAttribute('aria-busy');
        form.submit();
      });
  }

  function wireCards(row) {
    $$('[data-sts-card-form]', row).forEach(function (form) {
      if (form.__stsWired) return;
      form.__stsWired = 1;

      var card = form.closest ? form.closest('[data-sts-card]') : null;
      var add = $('.sts-card__add', form);
      var anyway = $('[data-sts-anyway]', form);

      if (add && add.tagName === 'BUTTON') {
        add.addEventListener('click', function (e) {
          e.preventDefault();
          addToCart(form, add);
        });
      }

      if (anyway && anyway.tagName === 'BUTTON') {
        anyway.addEventListener('click', function () {
          var reason = card ? trim(($('[data-sts-reason]', card) || {}).textContent) : '';
          confirmDialog(reason, function () {
            var prop = $('[data-sts-not-for-type]', form);
            if (prop && !prop.value) { prop.value = myType() || ''; prop.disabled = !prop.value; }
            addToCart(form, anyway, 'article_shortcode_buy_anyway');
          }, null, !!card && card.getAttribute('data-sts-fit') === 'subtype');
        });
      }
    });
  }

  /* ---------------------------------------------------------------- the CTA */

  /* [[ctaquiz]] on the standard template is already server-rendered into the body by
     sections/sts-article.liquid, so this only ever runs on Bloggle. The four states are all
     in the <template>; the <head> rules from snippets/sts-head-state.liquid pick one off
     <html data-sts-state> before the clone is even attached, so there is no flash.

     assets/sts-state.js fills its two runtime slots at boot and on `sts:state`, and this
     clone arrives after both, so they are filled here instead of re-dispatching an event
     the rest of the page would also hear. */
  function renderCta(root) {
    var tpl = $('template[data-sts-ctaquiz]');
    if (!tpl || !tpl.content) {
      /* No template on the page: leave the root alone rather than print a call to action
         that cannot know which state the reader is in. */
      root.classList.add('no-products');
      return;
    }

    var node = tpl.content.cloneNode(true);
    var state = (w.STS && w.STS.state) || {};
    var quiz = state.quiz || {};

    $$('[data-sts-type-code]', node).forEach(function (el) {
      if (!trim(el.textContent) && state.type) el.textContent = state.type;
    });
    $$('[data-sts-quiz-pct]', node).forEach(function (el) {
      el.textContent = quiz.pct == null ? '' : String(quiz.pct);
    });

    root.innerHTML = '';
    root.appendChild(node);
    root.classList.add('rendered');
  }

  /* ------------------------------------------------------------- the product row */

  function showEmpty(root, message) {
    if (message) {
      root.classList.add('show-message');
      root.textContent = configured().emptyMessage;
    } else {
      root.classList.add('no-products');
      root.innerHTML = '';
    }
  }

  /* One request per token, in order, so the row reads in the order the author wrote.
     A token that fails or matches nothing is skipped; one dead tag never empties a row
     that its neighbours filled. */
  function collect(command, section) {
    var seen = {};
    var items = [];
    var shell = null;
    var reasons = null;
    var perToken = Math.max(1, Math.round(command.total / command.tokens.length));

    return command.tokens.reduce(function (chain, token) {
      return chain.then(function () {
        if (items.length >= command.total) return null;
        var url = isSku(token) ? skuUrl(token, perToken, section) : tagUrl(token, section);

        return fetchSection(url).then(function (doc) {
          if (!shell) shell = doc.querySelector('[data-sts-shortcode-row]');
          if (!reasons) {
            var block = doc.querySelector('[data-sts-reasons]');
            if (block) {
              try { reasons = JSON.parse(block.textContent); } catch (e) { reasons = null; }
            }
          }

          var found = doc.querySelectorAll('[data-sts-shortcode-item]');
          var taken = 0;
          for (var i = 0; i < found.length && taken < perToken && items.length < command.total; i++) {
            var id = found[i].getAttribute('data-sts-shortcode-item');
            /* A product that two of this shortcode's tags both return appears once. */
            if (seen[id]) continue;
            seen[id] = 1;
            items.push(found[i]);
            taken++;
          }
        }, function (error) {
          warn('could not load "' + token + '"', error);
        });
      });
    }, Promise.resolve()).then(function () {
      return { shell: shell, items: items, reasons: reasons };
    });
  }

  function renderRow(root, command) {
    var section = configured().section;

    return collect(command, section).then(function (result) {
      if (!result.shell || !result.items.length) {
        showEmpty(root, command.message);
        return;
      }

      /* The shell and the items come from one or more parsed documents; they are imported
         into this one before anything is attached, so nothing is ever adopted twice. */
      var row = d.importNode(result.shell, true);
      var list = row.querySelector('[data-sts-shortcode-list]');
      if (!list) {
        showEmpty(root, command.message);
        return;
      }

      list.innerHTML = '';
      result.items.forEach(function (item) { list.appendChild(d.importNode(item, true)); });

      root.innerHTML = '';
      root.appendChild(row);
      root.classList.add('rendered');
      resolveCards(row, result.reasons);
      wireCards(row);

      /* A row is built after workstream 02 has resolved the session, but the session can
         change under it — a sign-in, a quiz result attaching, the staff state simulator.
         Subscribing rather than reading once is what assets/sts-collection.js does, and it
         means a reader who signs in mid-article is not left with a stale verdict. */
      try {
        if (w.STS && typeof w.STS.onState === 'function') {
          var first = true;
          w.STS.onState(function () {
            if (first) { first = false; return; }
            resolveCards(row, result.reasons);
          });
        }
      } catch (e) { warn('state subscription failed', e); }

      /* The arrows are the shared scroller's, wired by assets/sts-ui.js. */
      try {
        if (w.STS && w.STS.ui && typeof w.STS.ui.init === 'function') w.STS.ui.init(row);
      } catch (e) { warn('scroller wiring failed', e); }
    });
  }

  /* ------------------------------------------------------------------- boot */

  var roots = [];

  function cacheElements() {
    var found = $$(ROOTS);
    roots = [];
    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      /* A skeleton stands in for the root that owns it. */
      if (el.classList.contains('productCard--loader')) {
        el = el.closest ? el.closest('.article__product-slider--root') : null;
      }
      if (el && el.hasAttribute('data-tags') && roots.indexOf(el) === -1) roots.push(el);
    }
    return roots;
  }

  function renderSliders() {
    if (!roots.length) cacheElements();

    return roots.reduce(function (chain, root) {
      /* Both templates can ask twice — article.js boots on its own and the Bloggle
         enhancer calls in once it has built the roots — so a root is rendered once. */
      if (root.__stsShortcode) return chain;
      root.__stsShortcode = 1;

      var raw = root.getAttribute('data-tags') || '';
      if (raw.toLowerCase().indexOf('ctaquiz') > -1) {
        renderCta(root);
        return chain;
      }

      var command = parseCommand(raw);
      if (!command.tokens.length) {
        showEmpty(root, command.message);
        return chain;
      }

      return chain.then(function () {
        return renderRow(root, command).catch(function (error) {
          warn('row "' + raw + '" failed', error);
          showEmpty(root, command.message);
        });
      });
    }, Promise.resolve());
  }

  /* sections/sts-bloggle-enhance.liquid and assets/sts-bloggle-enhance.js call both by
     name once they have rewritten the Bloggle body, so these stay global. */
  w.cacheElements = cacheElements;
  w.renderSliders = renderSliders;

  function boot() {
    cacheElements();
    if (roots.length) renderSliders();
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})(window, document);
